import * as React from "react";
import { format } from "date-fns";
import {
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    tooltipClasses,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { Link as RouteLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import DateRangeIcon from "@mui/icons-material/DateRange";
import styled from "styled-components";
import AvatarBadge from "src/components/avatar-badge";
import ShowWithSkeleton from "src/utils/show-with-skeleton";
import Icon from "src/components/icon";
import githubIcon from "src/assets/icons/github-int";
import gitlabIcon from "src/assets/icons/gitlab-int";
import slackIcon from "src/assets/icons/slack-int";
import gitlabSelfManagedIcon from "src/assets/icons/gitlab-selfmanaged-int";
import googleWorkspaceIcon from "src/assets/icons/google-workspace-int";
import RangePickerModal from "src/components/range-picker-modal";
import jiraIcon from "src/assets/icons/jira-int";
import notionIcon from "src/assets/icons/notion-int";
import { Helmet } from "react-helmet";
import {
    TeamSprintsContextProvider,
    useTeamSprints,
    type Sprint,
} from "src/scenes/teams/dashboard/selected-sprints-provider-teams";
import { HTMLComponentTypes } from "src/components/template/constants";
import JoinLeftIcon from "@mui/icons-material/JoinLeft";
import {
    EditModes,
    useTemplateViewEdit,
} from "src/components/providers/template-view-edit-provider";
import TemplateDashboardTitle from "src/components/template/template-dashboard-title";
import Typography from "@mui/material/Typography";
import PlaylistAddCircleIcon from "@mui/icons-material/PlaylistAddCircle";
import ViewListIcon from "@mui/icons-material/ViewList";
import { ActiveDate } from "src/scenes/dashboard/date-provider";
import JiraProjectSelector from "src/components/template/jira-project-selector";
import request from "src/utils/request";
import { useTeam } from "../team/team-provider";

const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const SprintNotificationDialog = ({
    teamId,
    open,
    onClose,
}: {
    teamId: string;
    open: boolean;
    onClose: () => void;
}) => {
    const [recipients, setRecipients] = React.useState<{ email: string }[]>([]);
    const [savedRecipients, setSavedRecipients] = React.useState<{ email: string }[]>([]);
    const [input, setInput] = React.useState("");
    const [saving, setSaving] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    const hasChanges = React.useMemo(() => {
        const a = recipients
            .map((r) => r.email)
            .sort()
            .join(",");
        const b = savedRecipients
            .map((r) => r.email)
            .sort()
            .join(",");
        return a !== b;
    }, [recipients, savedRecipients]);

    React.useEffect(() => {
        if (!open) return;
        setLoading(true);
        request(`/api/jira/notification-config?teamId=${teamId}`, { method: "GET" })
            .then((data: unknown) => {
                const loaded = (data as { recipients?: { email: string }[] }).recipients ?? [];
                setRecipients(loaded);
                setSavedRecipients(loaded);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [open, teamId]);

    const addEmail = () => {
        const trimmed = input.trim().toLowerCase();
        if (!isValidEmail(trimmed) || recipients.some((r) => r.email === trimmed)) return;
        setRecipients((prev) => [...prev, { email: trimmed }]);
        setInput("");
    };

    const handleSave = () => {
        setSaving(true);
        request(`/api/jira/notification-config?teamId=${teamId}`, {
            method: "PUT",
            body: JSON.stringify({ recipients }),
        })
            .then(() => onClose())
            .catch(() => null)
            .finally(() => setSaving(false));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontSize: 15, fontWeight: 600 }}>
                Sprint Report Notifications
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: 12, opacity: 0.65, mb: 2 }}>
                    These recipients receive an email when a sprint starts (Planning report) or
                    closes (Review report).
                </Typography>
                {loading ? (
                    <Stack alignItems="center" py={3}>
                        <CircularProgress size={24} />
                    </Stack>
                ) : (
                    <>
                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                            <TextField
                                size="small"
                                placeholder="email@example.com"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        addEmail();
                                    }
                                }}
                                sx={{ flex: 1 }}
                            />
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={addEmail}
                                disabled={!isValidEmail(input.trim())}
                            >
                                Add
                            </Button>
                        </Stack>
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                            {recipients.map((r) => (
                                <Chip
                                    key={r.email}
                                    label={r.email}
                                    size="small"
                                    onDelete={() =>
                                        setRecipients((prev) =>
                                            prev.filter((x) => x.email !== r.email)
                                        )
                                    }
                                    sx={{ fontSize: 11 }}
                                />
                            ))}
                            {recipients.length === 0 && (
                                <Typography sx={{ fontSize: 12, opacity: 0.5 }}>
                                    No recipients configured.
                                </Typography>
                            )}
                        </Stack>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || !hasChanges}
                >
                    {saving ? "Saving…" : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const NavButton = styled(Button).withConfig({
    shouldForwardProp: (prop) => prop !== "active",
})`
    & > h1 {
        font-size: 13px;
        margin: 0;
        padding: 0;
    }
    font-size: 13px;
    min-height: 41px;
    text-transform: none;
    cursor: pointer;
    margin: 12px;
    min-width: 126px;
    color: ${({ theme, active }) =>
        active ? theme.palette.primary.main : theme.palette.text.primary};
    border: 1px solid
        ${({ theme, active }) => (active ? theme.palette.primary.main : "transparent")};
    border-radius: 6px;

    &:hover {
        color: ${({ theme }) => theme.palette.primary.main};
        border: 1px solid
            ${({ theme, active }) => (active ? theme.palette.primary.main : "transparent")};
        background-color: transparent;
    }
`;

const UnregisteredIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 42px;
    height: 42px;
    color: ${({ theme }) => theme.palette.background.default};
    background-color: ${({ theme }) => theme.palette.primary.main};
    border-radius: 50%;
`;

const StyledAvatarBadge = styled(AvatarBadge)`
    box-shadow: 0 4px 4px 0 #00000040;
    margin-left: ${({ firstElement }) => (firstElement ? "0" : "-8px")};
    z-index: 1;
    cursor: pointer;
    outline: ${({ isIntegrated, theme }) =>
        isIntegrated.gitlab || isIntegrated.github || isIntegrated.slack
            ? `solid 2px ${theme.palette.primary.main}`
            : `solid 2px ${theme.palette.background.inactiveIcons}`};
    &:hover {
        z-index: 2;
    }
`;

const Link = styled(RouteLink)`
    font-size: 12px;
    font-weight: 400;
    margin: 0;
    color: ${({ theme }) => theme.palette.primary.main};
    text-decoration: none;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        text-decoration: underline;
    }
`;

const TeamName = styled.h1`
    font-size: 20px;
    font-weight: 600;
    line-height: 20px;
    font-style: normal;
    color: ${({ theme }) => theme.palette.text.primary};
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;

const TeamNameLink = styled(RouteLink)`
    font-size: 20px;
    font-weight: 600;
    line-height: 20px;
    font-style: normal;
    color: ${({ theme }) => theme.palette.text.primary};
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;

const SectionHelper = styled.p`
    font-size: 12px;
    font-style: normal;
    font-weight: 200;
    opacity: 0.7;
    padding-right: 12px;
    margin: 0;
`;

const DatepickerButton = styled(Button)`
    font-size: 12px;
    width: 257px;
    z-index: 10;
`;

const LightTooltip = styled(({ className, isExtra, title, children, ...props }) => (
    <Tooltip {...props} title={title} classes={{ popper: className }}>
        {children}
    </Tooltip>
))(({ theme, isExtra }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.background.tooltip,
        border: `4px solid ${theme.palette.background.outline}`,
        borderRadius: 14,
        fontSize: 11,
        minWidth: isExtra ? "336px" : "300px",
    },
}));

const TeamRangePickerModal = ({ open, handleClose, toggleDatepicker }) => {
    return (
        <RangePickerModal
            open={open}
            handleClose={handleClose}
            toggleDatepicker={toggleDatepicker}
            isRepo={false}
            isTeams
            defaultTab="dates"
        />
    );
};

const PROJECT_QUERY_VARIABLES_SETTER = {
    project: { type: HTMLComponentTypes.dropdown, dynamicOptions: "$data.projects[*].projectKey" },
};

const formatDateRange = (from: Date | null, to: Date | null) => {
    if (!from || !to) return "Select Dates";
    const sameYear = from.getFullYear() === to.getFullYear();
    return sameYear
        ? `${format(from, "d MMM")} – ${format(to, "d MMM yyyy")}`
        : `${format(from, "d MMM yyyy")} – ${format(to, "d MMM yyyy")}`;
};

const SprintSelector = ({ toggleDatepicker }: { toggleDatepicker: () => void }) => {
    const {
        allSprints,
        selectedSprints,
        handleSprintChange,
        isScrum,
        sprintMode,
        setSprintMode,
        selectedProject,
        handleProjectChange,
        hasTicketIntegration,
    } = useTeamSprints();
    const { handleChange: handleDateChange, fromDate, toDate } = React.useContext(ActiveDate);
    const mode = sprintMode ?? "sprints";
    const dateLabel = formatDateRange(fromDate, toDate);

    const selectedIds = (selectedSprints ?? []).map((s: Sprint) => s.id);

    const handleSprintSelect = (event: { target: { value: unknown } }) => {
        const values = event.target.value as string[];
        if (!values.length) return;
        const selected = (allSprints ?? []).filter((s: Sprint) => values.includes(s.id));
        handleSprintChange?.(selected);
        const sorted = [...selected].sort(
            (a: Sprint, b: Sprint) =>
                new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
        );
        handleDateChange(
            new Date(sorted[0].startDate),
            new Date(sorted[sorted.length - 1].endDate)
        );
    };

    const handleModeChange = (
        _: React.MouseEvent<HTMLElement>,
        newMode: "sprints" | "dates" | null
    ) => {
        if (!newMode) return;
        setSprintMode?.(newMode);
        if (newMode === "dates") toggleDatepicker();
    };

    let displayLabel: string;
    if (!selectedSprints?.length) {
        displayLabel = "Select sprint";
    } else if (selectedSprints.length === 1) {
        displayLabel = selectedSprints[0].name;
    } else {
        displayLabel = `${selectedSprints.length} sprints`;
    }

    const projectSelector = hasTicketIntegration ? (
        <JiraProjectSelector
            queryVariablesSetter={PROJECT_QUERY_VARIABLES_SETTER}
            value={selectedProject ?? "All"}
            setValue={handleProjectChange ?? (() => {})}
        />
    ) : null;

    if (!isScrum) {
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <DatepickerButton startIcon={<DateRangeIcon />} onClick={toggleDatepicker}>
                    {dateLabel}
                </DatepickerButton>
                {projectSelector}
            </Stack>
        );
    }

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <ToggleButtonGroup value={mode} exclusive size="small" onChange={handleModeChange}>
                <ToggleButton value="sprints" sx={{ fontSize: 11, textTransform: "none", px: 1.5 }}>
                    Sprints
                </ToggleButton>
                <ToggleButton value="dates" sx={{ fontSize: 11, textTransform: "none", px: 1.5 }}>
                    Date Range
                </ToggleButton>
            </ToggleButtonGroup>

            {mode === "sprints" && (
                <FormControl size="small" sx={{ minWidth: 180, maxWidth: 260 }}>
                    <Select
                        multiple
                        value={selectedIds}
                        onChange={handleSprintSelect}
                        displayEmpty
                        renderValue={() => (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Icon svg={jiraIcon} width="14px" height="14px" />
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {displayLabel}
                                </Typography>
                            </Stack>
                        )}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                    >
                        {(allSprints ?? []).map((sprint: Sprint) => (
                            <MenuItem key={sprint.id} value={sprint.id} dense>
                                <Checkbox size="small" checked={selectedIds.includes(sprint.id)} />
                                <ListItemText
                                    primary={sprint.name}
                                    primaryTypographyProps={{ fontSize: 12 }}
                                />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {mode === "dates" && (
                <DatepickerButton startIcon={<DateRangeIcon />} onClick={toggleDatepicker}>
                    {dateLabel}
                </DatepickerButton>
            )}

            {projectSelector}
        </Stack>
    );
};

const TeamDashboard = () => {
    const [openDatepicker, setOpenDatepicker] = React.useState(false);
    const [notifOpen, setNotifOpen] = React.useState(false);
    const { teamId } = useParams();
    const { pathname } = useLocation();
    const pathnameSplit = pathname.split("/");
    const path = pathnameSplit && pathnameSplit[pathnameSplit.length - 1];
    const teamApi = useTeam();

    const { onEditMode, view, editView } = useTemplateViewEdit();
    const mergeMembersAndTempMembers = () => {
        if (teamApi && teamApi.status !== "SUCCESS") return null;
        const { members, tempMembers } = teamApi && teamApi.data;
        const normalizedTempMembers = tempMembers.map((temp) => ({
            displayName: temp.displayName,
            email: temp.email,
            isOwner: false,
            isIntegrated: temp.isIntegrated,
            jobTitle: temp.jobTitle || null,
            picture: null,
            id: null,
            isInvited: temp.isInvited,
        }));

        const combinedUsers = [...members, ...normalizedTempMembers];
        return combinedUsers;
    };

    const allMembers = mergeMembersAndTempMembers();

    const teamMembersToShow = allMembers ? allMembers.slice(0, 4) : [];
    const teamMembersHidden = allMembers ? allMembers.slice(4) : [];

    const toggleDatepicker = () => setOpenDatepicker(!openDatepicker);

    const navigate = useNavigate();

    const redirectToTemplatesDetails = () => {
        return navigate("/templates-details");
    };

    const tooltipContent = () => {
        return teamMembersHidden.map((member) => (
            <Stack direction="row" key={member.email} padding={2} alignItems="center" spacing={2}>
                {member.id ? (
                    <StyledAvatarBadge
                        name={member.displayName || member.email}
                        picture={member.picture}
                        isIntegrated={member.isIntegrated}
                        d
                    />
                ) : (
                    <UnregisteredIconWrapper>
                        <JoinLeftIcon />
                    </UnregisteredIconWrapper>
                )}
                <Stack direction="column">
                    <SectionHelper sx={{ marginLeft: "60px" }}>
                        {member.displayName || member.email}
                    </SectionHelper>
                    <Stack direction="row" spacing={2} pt={0.5}>
                        <Icon
                            svg={gitlabIcon}
                            fill={
                                member.isIntegrated.gitlab === true
                                    ? "palette.primary.main"
                                    : "palette.background.inactiveIcons"
                            }
                        />
                        <Icon
                            svg={gitlabSelfManagedIcon}
                            fill={
                                member.isIntegrated.gitlabSelfManaged === true
                                    ? "palette.primary.main"
                                    : "palette.background.inactiveIcons"
                            }
                        />
                        <Icon
                            svg={githubIcon}
                            fill={
                                member.isIntegrated.github === true
                                    ? "palette.primary.main"
                                    : "palette.background.inactiveIcons"
                            }
                        />
                        <Icon
                            svg={slackIcon}
                            fill={
                                member.isIntegrated.slack === true
                                    ? "palette.primary.main"
                                    : "palette.background.inactiveIcons"
                            }
                        />
                        <Icon
                            svg={googleWorkspaceIcon}
                            fill={
                                member.isIntegrated.google === true || member.isIntegrated.microsoft
                                    ? "palette.primary.main"
                                    : "palette.background.inactiveIcons"
                            }
                        />
                        <Icon
                            svg={jiraIcon}
                            fill={
                                member.isIntegrated.jira === true
                                    ? "palette.primary.main"
                                    : "palette.background.inactiveIcons"
                            }
                        />
                        <Icon
                            svg={notionIcon}
                            fill={
                                member.isIntegrated.notion === true
                                    ? "palette.primary.main"
                                    : "palette.background.inactiveIcons"
                            }
                        />
                    </Stack>
                </Stack>
            </Stack>
        ));
    };
    return (
        <TeamSprintsContextProvider>
            <Helmet>
                {teamApi.data && teamApi.data.name && (
                    <title>Adadot - {teamApi.data && teamApi.data.name}&apos;s Dashboard</title>
                )}
            </Helmet>
            <Box sx={{ width: "100%", typography: "body1" }}>
                <Grid container justifyContent="space-between" alignItems="center" m={2} mt={0}>
                    <Grid item>
                        <ShowWithSkeleton
                            fetchAction={teamApi}
                            height={25}
                            width={150}
                            variant={undefined}
                            streamingProperty={undefined}
                        >
                            <TeamNameLink to={`/teams/${teamId}`}>
                                <TeamName>{teamApi.data && teamApi.data.name}</TeamName>
                            </TeamNameLink>
                        </ShowWithSkeleton>
                    </Grid>
                    <Grid item>
                        <Stack
                            direction="row"
                            spacing={2}
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Stack direction="row" alignItems="center">
                                <SectionHelper> Showing data for: </SectionHelper>
                                {teamMembersToShow.map((member, index) => (
                                    <LightTooltip
                                        key={member.id}
                                        title={
                                            <Stack direction="column" padding={2}>
                                                {member.displayName || member.email || ""}
                                                {member.jobTitle ? ` is a ${member.jobTitle}` : ""}
                                                <Stack
                                                    direction="row"
                                                    spacing={2}
                                                    pt={0.5}
                                                    justifyContent="center"
                                                >
                                                    <Icon
                                                        svg={gitlabIcon}
                                                        fill={
                                                            member.isIntegrated.gitlab === true
                                                                ? "palette.primary.main"
                                                                : "palette.background.inactiveIcons"
                                                        }
                                                    />
                                                    <Icon
                                                        svg={gitlabSelfManagedIcon}
                                                        fill={
                                                            member.isIntegrated
                                                                .gitlabSelfManaged === true
                                                                ? "palette.primary.main"
                                                                : "palette.background.inactiveIcons"
                                                        }
                                                    />
                                                    <Icon
                                                        svg={githubIcon}
                                                        fill={
                                                            member.isIntegrated.github === true
                                                                ? "palette.primary.main"
                                                                : "palette.background.inactiveIcons"
                                                        }
                                                    />
                                                    <Icon
                                                        svg={slackIcon}
                                                        fill={
                                                            member.isIntegrated.slack === true
                                                                ? "palette.primary.main"
                                                                : "palette.background.inactiveIcons"
                                                        }
                                                    />
                                                    <Icon
                                                        svg={googleWorkspaceIcon}
                                                        fill={
                                                            member.isIntegrated.google === true ||
                                                            member.isIntegrated.microsoft
                                                                ? "palette.primary.main"
                                                                : "palette.background.inactiveIcons"
                                                        }
                                                    />
                                                    <Icon
                                                        svg={jiraIcon}
                                                        fill={
                                                            member.isIntegrated.jira === true
                                                                ? "palette.primary.main"
                                                                : "palette.background.inactiveIcons"
                                                        }
                                                    />
                                                    <Icon
                                                        svg={notionIcon}
                                                        fill={
                                                            member.isIntegrated.notion === true
                                                                ? "palette.primary.main"
                                                                : "palette.background.inactiveIcons"
                                                        }
                                                    />
                                                </Stack>
                                            </Stack>
                                        }
                                    >
                                        <span>
                                            {member.id ? (
                                                <StyledAvatarBadge
                                                    name={member.displayName || member.email}
                                                    picture={member.picture}
                                                    isIntegrated={member.isIntegrated}
                                                    firstElement={index === 0}
                                                />
                                            ) : (
                                                <UnregisteredIconWrapper>
                                                    <JoinLeftIcon />
                                                </UnregisteredIconWrapper>
                                            )}
                                        </span>
                                    </LightTooltip>
                                ))}
                            </Stack>
                            {teamMembersHidden.length > 0 && (
                                <LightTooltip
                                    title={tooltipContent()}
                                    placement="bottom-end"
                                    isExtra
                                >
                                    <Link to={`/teams/${teamId}`}>
                                        +{teamMembersHidden.length} more members
                                    </Link>
                                </LightTooltip>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
                <Grid container direction="row" alignItems="center" justifyContent="space-between">
                    <Grid
                        item
                        container
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ width: "auto" }}
                    >
                        <Grid item md={12}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                style={{ width: "100%" }}
                            >
                                <Typography style={{ fontSize: "13px" }}>
                                    Team dashboards
                                </Typography>
                                {path !== "insights" && (
                                    <SprintSelector toggleDatepicker={toggleDatepicker} />
                                )}
                            </Stack>
                        </Grid>
                        <Grid
                            item
                            container
                            direction="row"
                            alignItems="center"
                            justifyContent="flex-start"
                            sx={{ width: "auto" }}
                            md={9}
                        >
                            {!onEditMode && (
                                <NavButton
                                    variant="outlined"
                                    active={path === "insights"}
                                    component={RouteLink}
                                    to={`/teams/${teamId}/dashboard/insights`}
                                >
                                    {path === "insights" ? <h1>Insights</h1> : <>Insights</>}
                                </NavButton>
                            )}
                            {view &&
                                view.dashboards.map((dashboard, dashboardIndex) => (
                                    <TemplateDashboardTitle
                                        key={dashboard.id}
                                        dashboardIndex={dashboardIndex}
                                        dashboard={dashboard}
                                    />
                                ))}
                            {onEditMode && view && (
                                <Button
                                    startIcon={<PlaylistAddCircleIcon />}
                                    variant="contained"
                                    size="small"
                                    style={{ borderRadius: "24px", marginLeft: "26px" }}
                                    onClick={() => {
                                        if (editView) {
                                            editView({
                                                dashboardIndex: view.dashboards.length,
                                                editMode: EditModes.addDashboard,
                                            });
                                        }
                                    }}
                                >
                                    ADD DASHBOARD
                                </Button>
                            )}
                        </Grid>
                        <Grid item md={3}>
                            <Grid container alignItems="center" justifyContent="flex-end">
                                {!onEditMode ? (
                                    <Grid item>
                                        <Stack
                                            direction="column"
                                            alignItems="center"
                                            justifyContent="flex-end"
                                            marginRight={1}
                                        >
                                            <Stack
                                                direction="row"
                                                justifyContent="flex-end"
                                                alignItems="center"
                                            >
                                                {path === "sprint-review" && teamId && (
                                                    <Tooltip title="Configure sprint notifications">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setNotifOpen(true)}
                                                        >
                                                            <NotificationsIcon
                                                                sx={{ fontSize: 20 }}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Button
                                                    startIcon={<ViewListIcon />}
                                                    onClick={() => {
                                                        redirectToTemplatesDetails();
                                                    }}
                                                    style={{ width: "257px", fontSize: "12px" }}
                                                >
                                                    View all templates
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    </Grid>
                                ) : null}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Outlet />
                <TeamRangePickerModal
                    open={openDatepicker}
                    handleClose={() => setOpenDatepicker(false)}
                    toggleDatepicker={toggleDatepicker}
                />
                {teamId && (
                    <SprintNotificationDialog
                        teamId={teamId}
                        open={notifOpen}
                        onClose={() => setNotifOpen(false)}
                    />
                )}
            </Box>
        </TeamSprintsContextProvider>
    );
};

export default TeamDashboard;
